// aifab — TourGuide
// Provides a step-by-step overlay tour for first-time users.
// Each step highlights a DOM element via data-tour="step-key" attribute,
// or shows a floating panel when no target element exists.

import { useState, useEffect, useRef, useCallback } from 'react';

// ── Tour content — one entry per page ────────────────────────────────────────
export const TOURS = {

  dashboard: [
    {
      id: 'dash-welcome', title: 'Welcome to Fabric', position: 'center',
      body: 'Fabric is an AI/ML execution platform. You train models on your own infrastructure — no data leaves your network. This tour walks you through the key screens.',
    },
    {
      id: 'dash-stats', target: '[data-tour="dash-stats"]', title: 'Platform Overview', position: 'bottom',
      body: 'These cards give a live count of projects, runs, trained models, and server capacity. Numbers animate on load.',
    },
    {
      id: 'dash-projects', target: '[data-tour="dash-projects"]', title: 'Recent Projects', position: 'right',
      body: 'Each project groups a dataset, a model family, and multiple training runs. You can have separate projects per client, use case, or experiment.',
    },
    {
      id: 'dash-servers', target: '[data-tour="dash-servers"]', title: 'Server Capacity', position: 'left',
      body: 'Fabric trains on remote servers you register. The capacity bar shows how many are available vs actively training right now.',
    },
    {
      id: 'dash-runs', target: '[data-tour="dash-runs"]', title: 'Recent Runs', position: 'left',
      body: 'Every training attempt is a run. Runs are numbered, versioned, and compared by metrics. This panel shows the latest across all projects.',
    },
  ],

  data: [
    {
      id: 'data-intro', title: 'Data Page', position: 'center',
      body: 'This is where you upload datasets, run column profiling, and configure feature engineering before training. Nothing leaves your infrastructure.',
    },
    {
      id: 'data-upload', target: '[data-tour="data-upload"]', title: 'Upload a Dataset', position: 'bottom',
      body: 'Upload CSVs up to 2 MB directly. For larger files, register the remote path — Fabric reads it from your server rather than copying it.',
    },
    {
      id: 'data-documents', target: '[data-tour="data-documents"]', title: 'Unstructured Documents', position: 'bottom',
      body: 'PDFs, Word files, and other documents become training data here: text is extracted, chunked, and turned into a text-column datasource for domain pre-training. Merge many files into one corpus, or process them individually.',
    },
    {
      id: 'data-profile', target: '[data-tour="data-profile"]', title: 'Run Profiling', position: 'bottom',
      body: 'Profiling inspects every column — detecting types, null counts, cardinality, and sample values. It runs on your remote server so large datasets stay local.',
    },
    {
      id: 'data-configure', target: '[data-tour="data-configure"]', title: 'Configure Features', position: 'left',
      body: 'After profiling you mark which columns are features, which is the target label, and how each should be encoded. This config is versioned and frozen when a run starts.',
    },
  ],

  models: [
    {
      id: 'models-intro', title: 'Models Page', position: 'center',
      body: 'Models are configurations: a family (DistilBERT, XGBoost…) plus hyperparameters. Each training run produces a new version with its own metrics and artifact.',
    },
    {
      id: 'models-list', target: '[data-tour="models-list"]', title: 'Model Configs', position: 'right',
      body: 'Each row is a model configuration. Expand it to see all trained versions, their F1 scores, and which version is the current champion.',
    },
    {
      id: 'models-champion', target: '[data-tour="models-champion"]', title: 'Champion Model', position: 'bottom',
      body: 'The champion is the version you promote as best. It is used for inference when you test. Only one version can be champion per config at a time.',
    },
    {
      id: 'models-card', target: '[data-tour="models-card"]', title: 'Model Card', position: 'left',
      body: 'Every version has a model card — training duration, hyperparameters, artifact location, and fingerprints for reproducibility auditing.',
    },
  ],

  runs: [
    {
      id: 'runs-intro', title: 'Runs Page', position: 'center',
      body: 'A run is a single training attempt. It links a model config, a dataset version, and a feature config — freezing all three so the run is perfectly reproducible.',
    },
    {
      id: 'runs-new', target: '[data-tour="runs-new"]', title: 'Create a Run', position: 'bottom',
      body: 'Click here to launch the run wizard. You pick which model, dataset, server, and optional plugins to use. The platform handles scheduling, execution, and metric collection.',
    },
    {
      id: 'runs-filter', target: '[data-tour="runs-filter"]', title: 'Filter by Status', position: 'bottom',
      body: 'Filter runs by state: Created (not started yet), Queued, Running, Completed, Failed, or Cancelled. Click a tab to narrow the list.',
    },
    {
      id: 'runs-row', target: '[data-tour="runs-row"]', title: 'Expand a Run', position: 'bottom',
      body: 'Click any run row to expand it. You will see the dataset, model, execution attempts, live training logs (refreshed every 10 s), and final metrics.',
    },
  ],

  plugins: [
    {
      id: 'plugins-intro', title: 'Plugins Page', position: 'center',
      body: 'Plugins are Python scripts that hook into the training pipeline at four stages: Pre-process, Train (custom script), Evaluate, and Post-process.',
    },
    {
      id: 'plugins-upload', target: '[data-tour="plugins-upload"]', title: 'Upload a Plugin', position: 'bottom',
      body: 'Upload any Python script. Fabric checksums it immediately — if the file changes before a run executes, training is blocked. This prevents silent environment drift.',
    },
    {
      id: 'plugins-template', target: '[data-tour="plugins-template"]', title: 'Pipeline Templates', position: 'right',
      body: 'A template bundles four plugin slots into a named configuration. Runs reference templates so upgrading creates a new version without breaking existing runs.',
    },
  ],

  servers: [
    {
      id: 'servers-intro', title: 'Servers Page', position: 'center',
      body: 'Fabric trains on servers you own and control. Register them here with SSH credentials. All training, profiling, and inference runs on these machines.',
    },
    {
      id: 'servers-add', target: '[data-tour="servers-add"]', title: 'Register a Server', position: 'bottom',
      body: 'Add a server by providing its hostname, SSH port, and credentials. Fabric stores the SSH key in Vault — never in the database or config files.',
    },
    {
      id: 'servers-test', target: '[data-tour="servers-test"]', title: 'Test Connection', position: 'left',
      body: 'Test the connection before using a server. Fabric verifies SSH access, checks the Python venv exists, and confirms disk space is adequate.',
    },
  ],

  catalog: [
    {
      id: 'catalog-intro', title: 'Model Catalog', position: 'center',
      body: 'The catalog lists every supported model family — Transformer, Classical ML, and LLM paths. Each family shows what run types it supports and what hardware it needs.',
    },
    {
      id: 'catalog-families', target: '[data-tour="catalog-families"]', title: 'Model Families', position: 'bottom',
      body: 'Each card shows the family name, supported run purposes (Classification, Domain Pre-training, NER…), best-use guidance, and hardware requirements. LLM families require a GPU server.',
    },
  ],

  lineage: [
    {
      id: 'lineage-intro', title: 'Lineage Page', position: 'center',
      body: 'Lineage traces the full provenance of every trained model — which dataset version, feature config, run parameters, and plugin versions produced each artifact.',
    },
    {
      id: 'lineage-graph', target: '[data-tour="lineage-graph"]', title: 'Experiment Graph', position: 'right',
      body: 'Each node is a trained version. Edges show cloning relationships. Hover a node to see its key metrics. This is the full audit trail for any model in production.',
    },
  ],

  'create-run': [
    {
      id: 'createrun-intro', title: 'Creating a Run', position: 'center',
      body: "A run needs four things: a purpose, a model, a dataset, and (optionally) a pipeline. This wizard walks through them in order - your choices show up on the right as you go.",
    },
    {
      id: 'createrun-steps', target: '[data-tour="createrun-steps"]', title: 'Wizard Steps', position: 'right',
      body: 'Track progress here. Completed steps get a checkmark and are clickable - jump back anytime to change an earlier choice without losing later ones.',
    },
    {
      id: 'createrun-dapt', target: '[data-tour="createrun-dapt"]', title: 'Domain-Adapted Checkpoints', position: 'right',
      body: 'If a Domain Pre-training run has completed for this project, its checkpoint appears here first. Picking one starts a brand-new model built on weights already adapted to your data.',
    },
    {
      id: 'createrun-configs', target: '[data-tour="createrun-configs"]', title: 'Existing Model Configs', position: 'right',
      body: "This is different from the checkpoints above: these are model configs you've already created (possibly built on a domain-adapted checkpoint). Selecting one starts a new run/version of that same config.",
    },
    {
      id: 'createrun-summary', target: '[data-tour="createrun-summary"]', title: 'Run Summary', position: 'left',
      body: 'Everything you pick across all steps collects here, so you can always see the full picture before creating the run.',
    },
    {
      id: 'createrun-submit', target: '[data-tour="createrun-submit"]', title: 'Create the Run', position: 'top',
      body: "Once every required step is filled in, this creates the run and dispatches it. Nothing is scheduled or executed until you click this.",
    },
  ],

  admin: [
    {
      id: 'admin-intro', title: 'Admin Panel', position: 'center',
      body: 'The admin panel manages users, project access, the job queue, and the audit log. Only Admins and Org Admins can reach this page.',
    },
    {
      id: 'admin-users', target: '[data-tour="admin-users"]', title: 'User Management', position: 'bottom',
      body: 'Invite users and assign roles: ML Engineer (trains models), Data Engineer (manages data pipelines), or Viewer (read-only). Role changes apply on the user\'s next request.',
    },
    {
      id: 'admin-audit', target: '[data-tour="admin-audit"]', title: 'Audit Log', position: 'bottom',
      body: 'Every login, create, update, delete, and job dispatch is recorded with a timestamp, user, IP, and changed values. Export for compliance review.',
    },
  ],
};

// ── Tooltip positioning ───────────────────────────────────────────────────────
function getTooltipStyle(rect, position) {
  const W = 360, margin = 18;
  const vw = window.innerWidth, vh = window.innerHeight;

  if (!rect || position === 'center') {
    return { position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-52%)', width:W };
  }

  let top, left;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top  + rect.height / 2;

  if (position === 'bottom') { top = rect.bottom + margin; left = cx - W / 2; }
  else if (position === 'top') { top = rect.top - 220 - margin; left = cx - W / 2; }
  else if (position === 'right') { top = cy - 110; left = rect.right + margin; }
  else /* left */ { top = cy - 110; left = rect.left - W - margin; }

  left = Math.max(margin, Math.min(left, vw - W - margin));
  top  = Math.max(margin, Math.min(top,  vh - 240 - margin));

  return { position:'fixed', top, left, width:W };
}

// ── Spotlight ─────────────────────────────────────────────────────────────────
function Spotlight({ rect }) {
  const pad = 8;
  return (
    <div style={{ position:'fixed', inset:0, zIndex:9998, pointerEvents:'none' }}>
      <svg width="100%" height="100%" style={{ position:'absolute', inset:0 }}>
        <defs>
          <mask id="sm">
            <rect width="100%" height="100%" fill="white" />
            {rect && <rect x={rect.left-pad} y={rect.top-pad}
              width={rect.width+pad*2} height={rect.height+pad*2} rx="8" fill="black" />}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(13,27,46,.68)" mask="url(#sm)" />
        {rect && <>
          <rect x={rect.left-pad} y={rect.top-pad}
            width={rect.width+pad*2} height={rect.height+pad*2}
            rx="8" fill="rgba(232,71,10,.06)"
            stroke="#E8470A" strokeWidth="2"
            style={{ filter:'drop-shadow(0 0 8px rgba(232,71,10,.55))' }} />
        </>}
      </svg>
    </div>
  );
}

// ── TourGuide ─────────────────────────────────────────────────────────────────
export default function TourGuide({ page, onClose }) {
  const steps = TOURS[page] || [];
  const [idx, setIdx]         = useState(0);
  const [rect, setRect]       = useState(null);
  const [visible, setVisible] = useState(false);

  const step = steps[idx];

  const measure = useCallback(() => {
    if (!step?.target) { setRect(null); return; }
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ behavior:'smooth', block:'nearest' });
    setTimeout(() => setRect(el.getBoundingClientRect()), 320);
  }, [step]);

  useEffect(() => {
    setVisible(false);
    setRect(null);
    const t = setTimeout(() => { measure(); setVisible(true); }, 80);
    return () => clearTimeout(t);
  }, [idx, measure]);

  useEffect(() => {
    const fn = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') go(1);
      if (e.key === 'ArrowLeft')  go(-1);
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  });

  const go = (dir) => {
    const next = idx + dir;
    if (next < 0) return;
    if (next >= steps.length) { onClose(); return; }
    setIdx(next);
  };

  if (!steps.length || !step) return null;

  const isLast  = idx === steps.length - 1;
  const tipStyle = getTooltipStyle(rect, step.position);

  return (
    <>
      <Spotlight rect={rect} />

      <div style={{
        ...tipStyle, zIndex:9999,
        background:'#FFFFFF', border:'1px solid #E4EBF5',
        borderRadius:14, overflow:'hidden',
        boxShadow:'0 24px 60px rgba(13,27,46,.25), 0 4px 16px rgba(13,27,46,.1)',
        opacity: visible ? 1 : 0,
        transition:'opacity .2s ease, transform .2s ease',
        transform: tipStyle.transform || (visible ? 'translateY(0)' : 'translateY(10px)'),
      }}>

        {/* Accent bar */}
        <div style={{ height:3, background:'linear-gradient(90deg,#E8470A,#FF5E1F 60%,rgba(255,94,31,0))' }} />

        {/* Header */}
        <div style={{
          padding:'13px 16px 10px',
          display:'flex', alignItems:'flex-start', justifyContent:'space-between',
          borderBottom:'1px solid #EEF2FA',
          background:'linear-gradient(180deg,#F9FAFB,#FFFFFF)',
        }}>
          <div>
            <div style={{ fontSize:9.5, fontWeight:700, letterSpacing:'.12em', color:'#E8470A', textTransform:'uppercase', marginBottom:3 }}>
              {idx + 1} / {steps.length}
            </div>
            <div style={{ fontSize:14.5, fontWeight:700, color:'#0D1B2E', letterSpacing:'-.01em' }}>
              {step.title}
            </div>
          </div>
          <button onClick={onClose} style={{
            background:'none', border:'none', color:'#94A3B8',
            fontSize:15, cursor:'pointer', padding:'2px 5px', lineHeight:1,
            borderRadius:4, marginTop:1,
          }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding:'12px 16px 10px' }}>
          <p style={{ fontSize:12.5, color:'#334155', lineHeight:1.7, margin:0 }}>{step.body}</p>
        </div>

        {/* Progress dots */}
        <div style={{ padding:'0 16px 10px', display:'flex', gap:5, alignItems:'center' }}>
          {steps.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} style={{
              width: i === idx ? 20 : 6, height:6, borderRadius:99,
              border:'none', cursor:'pointer', padding:0, flexShrink:0,
              background: i === idx ? '#E8470A' : i < idx ? '#FFB399' : '#D1DCF0',
              transition:'width .2s, background .2s',
            }} />
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding:'8px 16px 13px',
          display:'flex', alignItems:'center', justifyContent:'space-between',
          borderTop:'1px solid #EEF2FA',
        }}>
          <button onClick={onClose} style={{
            background:'none', border:'none', fontSize:11.5, color:'#94A3B8', cursor:'pointer', padding:0,
          }}>Skip tour</button>

          <div style={{ display:'flex', gap:7 }}>
            {idx > 0 && (
              <button onClick={() => go(-1)} style={{
                padding:'6px 13px', borderRadius:6,
                border:'1px solid #D1DCF0', background:'#FFFFFF',
                fontSize:12, fontWeight:600, color:'#475569', cursor:'pointer',
              }}>← Back</button>
            )}
            <button onClick={() => go(1)} style={{
              padding:'6px 16px', borderRadius:6, border:'none',
              background: isLast
                ? 'linear-gradient(135deg,#059669,#10B981)'
                : 'linear-gradient(135deg,#E8470A,#FF5E1F)',
              fontSize:12, fontWeight:700, color:'#FFFFFF', cursor:'pointer',
              boxShadow: isLast ? '0 2px 8px rgba(5,150,105,.3)' : '0 2px 8px rgba(232,71,10,.3)',
            }}>
              {isLast ? '✓  Done' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
